import React, { useCallback, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    CircularProgress,
    Grid,
    Tooltip,
    Typography
} from '@material-ui/core';
import { Link, useHistory, useLocation } from 'react-router-dom';
import TablePager from '../atom/TablePager';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../hooks/useSwaggerClient';
import AdminHeader from '../atom/AdminHeader';
import {
    ChartColumnIcon,
    CheckIcon,
    DeleteIcon,
    DuplicateIcon,
    EditIcon,
    TimesIcon,
    UsersIcon
} from '../../icons';
import ActionIconButton from '../atom/ActionIconButton';

function ClientListPage() {
    const history = useHistory();
    const location = useLocation();
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [deleteRender, rerenderDelete] = useState(false);

    const p = new URLSearchParams(location.search);
    const type = p.get('type') || '';
    const onlyVendors = type === 'vendor';
    const onlyClients = type === 'client';

    const [clients, loading, error, setClients] = useSwaggerClient(
        'clients',
        'listClients',
        {
            page,
            pageSize,
            search,
            onlyVendors,
            onlyClients
        },
        [deleteRender]
    );

    const duplicateClient = useCallback(
        clientId => {
            doSwaggerCall('clients', 'duplicateClient', { clientId })
                .then(({ client }) => {
                    notifySuccess('Client duplicated');
                    // This one is ugly
                    rerenderDelete(d => !d);
                    history.push(`/admin/client/${client.id}/edit`);
                })
                .catch(() => {
                    notifyError('Cannot duplicate client');
                });
        },
        [notifyError, notifySuccess]
    );

    const deleteClient = useCallback(
        clientId => {
            doSwaggerCall('clients', 'deleteClient', { clientId })
                .then(() => {
                    // we finished, remove user from list
                    notifySuccess('Client deleted');
                    // This one is ugly
                    rerenderDelete(d => !d);
                })
                .catch(() => {
                    notifyError('Cannot delete client');
                });
        },
        [setClients, clients, notifyError, notifySuccess]
    );

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error.response.body.message}</h1>
            </Grid>
        );
    }

    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader
                title={onlyVendors ? 'Vendors' : onlyClients ? 'Clients' : 'Clients and vendors'}
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
                createLabel="New client"
                onCreate={() => history.push('/admin/client/new')}
            />
            <Grid item container xs={12} justifyContent="center">
                {loading && clients?.clients.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <Table style={{ backgroundColor: 'white' }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients?.clients.map(client => (
                                <TableRow key={client.id}>
                                    {(client.is_vendor && (
                                        <TableCell>
                                            {client.allowed_selling ? (
                                                <Tooltip title="Can sell activities">
                                                    <CheckIcon />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Vendor, but cannot sell activities">
                                                    <TimesIcon />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    )) || <TableCell />}
                                    <TableCell>{client.name}</TableCell>
                                    <TableCell>
                                        {typeof client.contact_email?.reservation === 'string' &&
                                        client.contact_email.reservation.trim() !== ''
                                            ? client.contact_email.reservation.split(',').length ===
                                              1
                                                ? client.contact_email.reservation
                                                : `${
                                                      client.contact_email.reservation.split(',')[0]
                                                  } and ${client.contact_email.reservation.split(
                                                      ','
                                                  ).length - 1} other(s)`
                                            : ''}
                                    </TableCell>
                                    <TableCell>{client.client_url || ''}</TableCell>
                                    <TableCell style={{ width: 220 }}>
                                        <ActionIconButton
                                            title="Edit"
                                            icon={EditIcon}
                                            to={`/admin/client/${client.id}`}
                                        />
                                        <ActionIconButton
                                            title="View users"
                                            icon={UsersIcon}
                                            to={`/admin/client/${client.id}/users`}
                                        />
                                        <ActionIconButton
                                            title="View stats"
                                            icon={ChartColumnIcon}
                                            to={`/admin/client/stats/${client.id}`}
                                        />
                                        <ActionIconButton
                                            title="Duplicate"
                                            icon={DuplicateIcon}
                                            iconProps={{ style: { border: '1py solid black' } }}
                                            onClick={() => {
                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                    title: `Are you sure you want to duplicate ${client?.name}?`,
                                                    content: 'This action is irreversible.',
                                                    confirm: () => duplicateClient(client.id)
                                                });
                                            }}
                                        />
                                        <ActionIconButton
                                            title="Delete"
                                            icon={DeleteIcon}
                                            onClick={() => {
                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                    title: `Are you sure you want to remove ${client?.name}?`,
                                                    content: 'This action is irreversible.',
                                                    confirm: () => deleteClient(client.id)
                                                });
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <Grid container justifyContent="flex-start" style={{ paddingTop: '12px' }}>
                    <Link to="/admin/expiringdocuments" target="_blank">
                        <Typography style={{ cursor: 'pointer' }} color="primary">
                            View expiring documents
                        </Typography>
                    </Link>
                </Grid>
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={clients?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default ClientListPage;
